import { useCurrentTenant } from "@drift/oneplatfront";
import { useTranslation } from "@onefront/react-sdk";
import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";
import { Box, Divider, Stack } from "@vapor/react-material";
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DetailsSections } from "../../../../../@types/enums/details-sections/DetailsSections";
import { Classifier } from "../../../../../@types/enums/general/Classifier";
import SidebarMenu from "../../../../../components/general/sidebar-menu";
import { FF_INTERMEDIARIES } from "../../../../../utils/feature-flags";
import { WorkspaceSidePanelDetails } from "@onefront/workspace-ui";

const mockedSections = (t, classifier) => [
    {
        key: DetailsSections.Registry,
        label: t("details.sections.registry")
    },
    {
        key: DetailsSections.Offices,
        label: t("details.sections.offices")
    },
    {
        key: DetailsSections.Accounting,
        label: t("details.sections.accounting"),
        hidden: classifier !== Classifier.COMPANY
    },
    {
        key: DetailsSections.Business,
        label: t("details.sections.business"),
        hidden: classifier !== Classifier.COMPANY
    },
    {
        key: DetailsSections.AntimoneyLaundering,
        label: t("details.sections.antimoneylaundering")
    },
    {
        key: DetailsSections.Intrastat,
        label: t("details.sections.intrastat"),
        hidden: classifier !== Classifier.COMPANY
    },
    {
        key: DetailsSections.Banks,
        label: t("details.sections.creditLines")
    },
    ...(FF_INTERMEDIARIES
        ? [
              {
                  key: DetailsSections.Intermediaries,
                  label: t("details.sections.intermediaries")
              }
          ]
        : [])
];

interface DetailsSidebarProps {
    data?: any;
    section?: string;
    withoutMenu?: boolean;
    sections?: Array<any>;
    customDetails?: React.ReactNode;
    redirectUrl?: string;
    redirectUrlParams?: string;
    defaultSection?: string;
    loading?: boolean;
    noDetails?: boolean;
}

export const DetailsSidebar = ({
    data,
    section,
    sections,
    customDetails,
    withoutMenu,
    redirectUrl,
    redirectUrlParams,
    defaultSection,
    loading,
    noDetails = false
}: DetailsSidebarProps) => {
    const { t } = useTranslation();
    const { tenant } = useCurrentTenant(true);
    const navigate = useNavigate();
    const { search } = useLocation();
    const { workspaceId } = useParams();

    return (
        <Stack direction={"column"} spacing={2.5}>
            {!noDetails && customDetails && (
                <Box px={4} pt={4}>
                    {customDetails}
                </Box>
            )}
            {!noDetails && !customDetails && (
                <Box px={4} pt={4}>
                    {data && <WorkspaceSidePanelDetails workspace={data} />}
                </Box>
            )}
            {!withoutMenu && (
                <>
                    <Divider light />
                    <Box px={4} pt={2}>
                        <ExtendedTypography component={"div"} variant="titleSmall" color="primary.textTitleColor">
                            {t("details.sidebarMenuTitle")}
                        </ExtendedTypography>
                    </Box>

                    <Box px={4}>
                        <SidebarMenu
                            sections={sections || mockedSections(t, data?.classifier)}
                            activeSection={section || defaultSection}
                            onChange={sectionValue => {
                                if (sections) {
                                    if (redirectUrl) {
                                        navigate(`${redirectUrl}/${sectionValue}?${redirectUrlParams}`);
                                        return null;
                                    }
                                    return null;
                                }
                                navigate(`/details/${workspaceId || tenant?.id}/${sectionValue}${search}`);
                            }}
                            loading={loading}
                        />
                    </Box>
                </>
            )}
        </Stack>
    );
};

export default DetailsSidebar;
